/**
 * HKDSE Subject Categories Utility
 * 
 * Category A: Core and Elective subjects
 * Category B: Applied Learning subjects  
 * Category C: Other Languages
 */

import type { CategoryLimits } from '../../types';

// Import categories from shared JSON
import categoriesDataRaw from '../../../../data/subject_categories.json';

// Subject category mapping for HKDSE subjects
export const SUBJECT_CATEGORIES: { [subject: string]: 'A' | 'B' | 'C' } = categoriesDataRaw as { [subject: string]: 'A' | 'B' | 'C' };

// Legacy subject labels from institution calculators mapped to canonical subject names.
const normalizeSubjectNameForLookup = (subject: string): string => {
  return subject
    .normalize('NFKC')
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
};

export const SUBJECT_NAME_ALIASES: Record<string, string> = {
  'PR And Multimedia Communication': 'PR and Multimedia Communication',
  'Interior and Event Spatial Design': 'Interior and Exhibition Design',
  'Adv Dip of French Lang Studies/Dip of French Lang Studies': 'French',
  'French: Advanced Diploma of French Language Studies / Diploma of French Language Studies': 'French',
  'Goethe-Certificate': 'German',
  'German: Goethe-Certificate': 'German',
  'Japanese-Language Proficiency Test': 'Japanese',
  'Japanese: Japanese-Language Proficiency Test': 'Japanese',
  'Test of Proficiency in Korean II': 'Korean',
  'Korean: Test of Proficiency in Korean II': 'Korean',
  'Diploma of Spanish as a Foreign Language': 'Spanish',
  'Spanish: Diploma of Spanish as a Foreign Language': 'Spanish',
  'Urdu (International)': 'Urdu',
  'Urdu: Urdu (International)': 'Urdu',
  'Mathematics (Extended part - Calculus and Statistics)': 'Mathematics Extended Part Module 1',
  'Mathematics (Extended part - Algebra and Calculus)': 'Mathematics Extended Part Module 2',
  'Mathematics Extended Part Module I': 'Mathematics Extended Part Module 1',
  'Mathematics Extended Part Module II': 'Mathematics Extended Part Module 2',
  'Combined Science: Biology + Chemistry': 'Combined Science (Chemistry, Biology)',
  'Combined Science: Biology + Physics': 'Combined Science (Biology, Physics)',
  'Combined Science: Physics + Chemistry': 'Combined Science (Physics, Chemistry)',
  'Technology and Living (Fashion, Clothing and Textiles)': 'Technology and Living',
  'Technology and Living (Food Science and Technology)': 'Technology and Living',
  'Design and Applied Technology (DAT)': 'Design and Applied Technology',
  'DAT': 'Design and Applied Technology',
  'BAFS': 'Business, Accounting and Financial Studies',
  'ApL (Vocational English) - English Communication': 'Applied Learning (Vocational English) - English Communication',
  'ApL (Vocational English) - English for Business Services': 'Applied Learning (Vocational English) - English for Business Services',
  'ApL (Vocational English) - English for Service Professionals': 'Applied Learning (Vocational English) - English for Service Professionals',
  'Applied Learning (Vocational English) - English Communication': 'Applied Learning (Vocational English) – English Communication',
  'Applied Learning (Vocational English) - English for Business Services': 'Applied Learning (Vocational English) – English for Business Services',
  'Applied Learning (Vocational English) - English for Service Professionals': 'Applied Learning (Vocational English) – English for Service Professionals',
  'Digital Brand Communication': 'Digital Brand Communication/Digital Advertising',
  'Film and Transmedia': 'Film and Transmedia/Film Production',
  'Practical Translation (Chi-Eng)': 'Practical Translation (CHIN-ENG)',
  'Creative English – PR and Marketing': 'Creative English - PR and Marketing',
};

/**
 * Memoised because this is the hottest function in the scoring pass: roughly 2.6 million calls to
 * score all 422 programmes (~6,100 per programme), every one of them NFKC-normalising and running
 * two regexes over one of the same ~117 subject names. Caching it is about 950ms off a full pass.
 *
 * Safe to cache without any invalidation: it is a pure string -> string mapping over bundled data
 * that cannot change at runtime, and the key space is the fixed set of names in that data.
 */
const CANONICAL_NAME_CACHE = new Map<string, string>();

export const canonicalizeSubjectName = (subject: string): string => {
  const cached = CANONICAL_NAME_CACHE.get(subject);
  if (cached !== undefined) return cached;

  const normalizedSubject = normalizeSubjectNameForLookup(subject);
  const canonical =
    SUBJECT_NAME_ALIASES[subject] || SUBJECT_NAME_ALIASES[normalizedSubject] || normalizedSubject;

  CANONICAL_NAME_CACHE.set(subject, canonical);
  return canonical;
};

const CANONICAL_SUBJECT_CATEGORIES: { [subject: string]: 'A' | 'B' | 'C' } = Object.entries(SUBJECT_CATEGORIES)
  .reduce<{ [subject: string]: 'A' | 'B' | 'C' }>((acc, [subject, category]) => {
    const canonicalSubject = canonicalizeSubjectName(subject);
    if (acc[canonicalSubject] === undefined) {
      acc[canonicalSubject] = category;
    }
    return acc;
  }, {});

export const canonicalizeRecordSubjectKeys = <T>(record: Record<string, T>): Record<string, T> => {
  const canonicalized: Record<string, T> = {};

  for (const [subject, value] of Object.entries(record)) {
    const canonicalSubject = canonicalizeSubjectName(subject);
    const shouldOverwrite = canonicalized[canonicalSubject] === undefined || subject === canonicalSubject;
    if (shouldOverwrite) {
      canonicalized[canonicalSubject] = value;
    }
  }

  return canonicalized;
};

/**
 * Get the category of a subject (defaults to 'A' if unknown)
 */
export const getSubjectCategory = (subject: string): 'A' | 'B' | 'C' => {
  const normalizedSubject = normalizeSubjectNameForLookup(subject);
  const canonicalSubject = canonicalizeSubjectName(subject);
  return SUBJECT_CATEGORIES[subject]
    || SUBJECT_CATEGORIES[normalizedSubject]
    || SUBJECT_CATEGORIES[canonicalSubject]
    || CANONICAL_SUBJECT_CATEGORIES[canonicalSubject]
    || 'A';
};

/**
 * Check if a subject is allowed based on category limits
 */
export const isSubjectAllowedByCategory = (
  subject: string,
  categoryLimits: CategoryLimits | undefined,
  currentCategoryCounts: { A: number; B: number; C: number }
): boolean => {
  if (!categoryLimits) return true;

  const category = getSubjectCategory(subject);

  // Check allowed_categories
  if (categoryLimits.allowed_categories && !categoryLimits.allowed_categories.includes(category)) {
    return false;
  }

  // Check max_category_b
  if (category === 'B' && categoryLimits.max_category_b !== undefined) {
    if (currentCategoryCounts.B >= categoryLimits.max_category_b) {
      return false;
    }
  }

  // Check max_category_c
  if (category === 'C' && categoryLimits.max_category_c !== undefined) {
    if (currentCategoryCounts.C >= categoryLimits.max_category_c) {
      return false;
    }
  }

  // Check max_combined_bc
  if ((category === 'B' || category === 'C') && categoryLimits.max_combined_bc !== undefined) {
    if (currentCategoryCounts.B + currentCategoryCounts.C >= categoryLimits.max_combined_bc) {
      return false;
    }
  }

  return true;
};

/**
 * Filter and sort subjects based on category limits
 * Returns subjects sorted by score, filtered by category limits
 */
export const filterSubjectsByCategory = <T extends { subject: string; score: number }>(
  subjects: T[],
  categoryLimits: CategoryLimits | undefined,
  maxCount: number
): T[] => {
  if (!categoryLimits) {
    // No limits, just return top N by score
    return [...subjects]
      .sort((a, b) => b.score - a.score)
      .slice(0, maxCount);
  }

  // Sort by score descending
  const sorted = [...subjects].sort((a, b) => b.score - a.score);

  const result: T[] = [];
  const categoryCounts = { A: 0, B: 0, C: 0 };

  for (const item of sorted) {
    if (result.length >= maxCount) break;

    if (isSubjectAllowedByCategory(item.subject, categoryLimits, categoryCounts)) {
      result.push(item);
      const category = getSubjectCategory(item.subject);
      categoryCounts[category]++;
    }
  }

  return result;
};

/**
 * Institution data structure for max_subjects_per_category lookup
 */
interface InstitutionData {
  max_subjects_per_category?: { A?: number; B?: number; C?: number };
  [key: string]: any;
}

/**
 * Get max APL (Category B) subjects allowed for a programme
 * Uses max_subjects_per_category from institution/programme data.
 * -1 = unlimited, 0 = not accepted.
 * 
 * @param programmeMaxPerCat - Programme-specific max_subjects_per_category (from JupasProgram)
 * @param institutionData - Institution data object containing max_subjects_per_category
 * @param jupasId - JUPAS ID for fallback to hardcoded defaults
 * @returns Maximum number of Category B subjects allowed
 */
export const getMaxAplForProgramme = (
  programmeMaxPerCat: { A?: number; B?: number; C?: number } | undefined,
  institutionData: InstitutionData | undefined,
  jupasId: string
): number => {
  // 1. Programme-level override takes priority
  if (programmeMaxPerCat?.B !== undefined) {
    return programmeMaxPerCat.B;
  }
  
  // 2. Institution-level setting from data
  if (institutionData?.max_subjects_per_category?.B !== undefined) {
    return institutionData.max_subjects_per_category.B;
  }
  
  // 3. Fallback to 0 (not accepted)
  return 0;
};

/**
 * Get default category limits based on university (from JUPAS ID prefix)
 * These are applied when a programme doesn't specify its own category limits
 * 
 * @param jupasId - JUPAS programme ID
 * @param maxAplOverride - Optional override for max_category_b from programme/institution data
 * 
 * DEFAULT UNIVERSITY CATEGORY POLICIES:
 * 
 * HKU (6xxx):
 *   - Category B: 0 subjects (not counted in main admission score)
 *   - Category C: Max 1 subject (accepted as elective, converted score)
 *   - M1/M2: Accepted as full elective
 * 
 * CUHK (4xxx):
 *   - Category B: 0 subjects for Main Score (can be "extra" for bonus points only)
 *   - Category C: Max 1 subject (accepted as 2nd elective, unspecified elective)
 *   - M1/M2: Accepted as full elective
 * 
 * HKUST (5xxx):
 *   - Category B: 0 subjects (not counted)
 *   - Category C: Max 1 subject (accepted as 2nd elective for some programs)
 *   - M1/M2: Accepted, highly valued (often x1.5 or x2.0 weight)
 * 
 * PolyU (3xxx):
 *   - Category B: Max 1 subject (only "Attained with Distinction" accepted, only some programs)
 *   - Category C: Accepted (can count as elective)
 *   - M1/M2: Accepted as full elective
 * 
 * CityU (1xxx):
 *   - Category B: 0 subjects (General Rule, exception for ~10 specific programs like Creative Media)
 *   - Category C: Accepted (can count as elective)
 *   - M1/M2: Accepted as full elective
 * 
 * HKBU (2xxx):
 *   - GER: 1st elective must be Cat A excluding M1/M2; 2nd must be Cat A (incl. M1/M2).
 *     Individual programmes may additionally accept Cat B/C for the 2nd elective (PER).
 *   - Category B: Max 2 subjects in score calculation (where programme allows)
 *   - Category C: Max 2 subjects in score calculation (where programme allows)
 *   - M1/M2: Accepted as full elective (2nd GER slot / scoring)
 * 
 * Lingnan (7xxx):
 *   - Category B: 0 subjects for Main Score (used for "bonus points" or "tie-breaker" only)
 *   - Category C: Accepted (Grade E/E equivalent or above)
 *   - M1/M2: Accepted as full elective
 * 
 * EdUHK (8xxx):
 *   - Category B: Max 1 subject ("Attained with Distinction" counts as Level 3/4, 
 *                 specific ApL subjects may have weighting x1.5)
 *   - Category C: Accepted (can count as elective)
 *   - M1/M2: Accepted as full elective
 * 
 * HKMU (9xxx):
 *   - Category B: Accepted ("Attained" = Level 2, useful for meeting general entry requirements)
 *   - Category C: Accepted as full elective
 *   - M1/M2: Accepted as full elective
 * 
 * PROGRAM-SPECIFIC EXCEPTIONS (override university defaults):
 * 
 * A. Medicine & Health (MBBS/MBChB/Dentistry):
 *    - HKU MBBS (6456), Dentistry (6107): Category C EXCLUDED from "Best 6"; 
 *      M1/M2 recognized but Science subjects required
 *    - CUHK MBChB (4001, 4002): Category C EXCLUDED; M1/M2 EXCLUDED as main elective; 
 *      Category B EXCLUDED entirely
 * 
 * B. Engineering & Science:
 *    - HKUST Engineering/Science: Category C often EXCLUDED or given zero weight; 
 *      M1/M2 highly valued (x1.5 or x2.0)
 *    - PolyU/CityU Engineering: Category B generally EXCLUDED; prefer Science/Math electives
 * 
 * C. Programs Accepting Category B (exceptions to general exclusion):
 *    - CityU: BA Creative Media, BSc Creative Media, BAS New Media, BA Chinese & History
 *    - EdUHK: High acceptance for BEd programs if ApL subject is relevant (Visual Arts, Music, PE)
 *    - PolyU: Design, Fashion, Hotel & Tourism programs often accept relevant ApL with "Distinction"
 * 
 * D. "Category A Only" Programs:
 *    - Global Business (GBUS) at HKU/CUHK/HKUST: Effectively Category A only (require 3+ specific electives)
 *    - Law: Generally relies on high English + high Category A elective scores
 */
export const getDefaultCategoryLimits = (jupasId: string, maxAplOverride?: number): CategoryLimits | undefined => {
  if (!jupasId) return undefined;

  const prefix = jupasId.charAt(0);

  // Check for special programs first (Medicine, Dentistry, etc.)
  // CUHK Medicine: 4001 (MBChB), 4002 (GPS for Medicine)
  // HKU Medicine: 6456 (MBBS), HKU Dentistry: 6107 (BDS)
  if (['4001', '4002'].includes(jupasId) ||
    ['6456', '6107'].includes(jupasId) ||
    jupasId.startsWith('6') && jupasId.includes('MBBS')) {
    // Medicine/Dentistry - Category A only
    // These programs exclude both Category B and C from main score calculation
    return {
      max_category_b: 0,
      max_category_c: 0,
      allowed_categories: ['A']
    };
  }

  // Helper to get max_category_b: use override if provided, otherwise use institution default
  const getMaxB = (institutionDefault: number): number => {
    return maxAplOverride !== undefined ? maxAplOverride : institutionDefault;
  };

  switch (prefix) {
    case '6': // HKU
      return {
        max_category_b: getMaxB(0),
        max_category_c: 1,
        allowed_categories: getMaxB(0) > 0 ? ['A', 'B', 'C'] : ['A', 'C']
      };

    case '4': // CUHK
      return {
        max_category_b: getMaxB(0),
        max_category_c: 1,
        allowed_categories: getMaxB(0) > 0 ? ['A', 'B', 'C'] : ['A', 'C']
      };

    case '5': // HKUST
      return {
        max_category_b: getMaxB(0),
        max_category_c: 1,
        allowed_categories: getMaxB(0) > 0 ? ['A', 'B', 'C'] : ['A', 'C']
      };

    case '3': // PolyU
      return {
        max_category_b: getMaxB(1),
        max_category_c: 1,
        allowed_categories: ['A', 'B', 'C']
      };

    case '1': // CityU
      return {
        max_category_b: getMaxB(0),
        max_category_c: 1,
        allowed_categories: getMaxB(0) > 0 ? ['A', 'B', 'C'] : ['A', 'C']
      };

    case '2': // HKBU
      return {
        max_category_b: getMaxB(2),
        max_category_c: 2,
        allowed_categories: ['A', 'B', 'C']
      };

    case '7': // Lingnan
      return {
        max_category_b: getMaxB(0),
        max_category_c: 1,
        allowed_categories: getMaxB(0) > 0 ? ['A', 'B', 'C'] : ['A', 'C']
      };

    case '8': // EdUHK
      return {
        max_category_b: getMaxB(1),
        max_category_c: 1,
        allowed_categories: ['A', 'B', 'C']
      };

    case '9': // HKMU
      return {
        max_category_b: getMaxB(2),
        max_category_c: 2,
        allowed_categories: ['A', 'B', 'C']
      };

    case 'S': // SSSDP
      return {
        max_category_b: getMaxB(1),
        max_category_c: 1,
        allowed_categories: ['A', 'B', 'C']
      };

    default:
      // Unknown university - no limits
      return undefined;
  }
};

